// src/services/apiClient.js
import axios from 'axios';

// Base API URL
//const API_URL = 'http://localhost:5000/api';   // Use relative path for dev build
const API_URL = '/api/api'; // Use relative path for production build
// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh token requests
let isRefreshing = false;
// Store pending requests that should be retried after token refresh
let failedQueue = [];

// Process the queue of failed requests
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Add request interceptor to include token in headers
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    
    const originalRequest = error.config;
    
    // If the error is not 401 or the request already tried after refresh, reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    
    // Mark this request as retried to prevent infinite loops
    originalRequest._retry = true;
    
    // If already refreshing, add this request to queue
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers['Authorization'] = `Bearer ${token}`;
          return apiClient(originalRequest);
        })
        .catch(err => {
          return Promise.reject(err);
        });
    }
    
    isRefreshing = true;
    
    try {
      // Get refresh token from local storage
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        // No refresh token available, logout the user
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(new Error('Session expired. Please log in again.'));
      }
      
      // Call refresh token API
      const response = await axios.post(`${API_URL}/auth/refresh-token`, {
        refreshToken
      });
      
      // Update tokens in localStorage
      const { accessToken, refreshToken: newRefreshToken } = response.data;
      localStorage.setItem('accessToken', "");
      localStorage.setItem('refreshToken', "");
        // Uncomment the following lines if you want to store tokens in localStorage old code
      // localStorage.setItem('accessToken', accessToken);
      // localStorage.setItem('refreshToken', newRefreshToken);
      
      // Update header for the original request
      originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
      
      // Process any queued requests
      processQueue(null, accessToken);
      
      return apiClient(originalRequest);
    } catch (err) {
      // Process queued requests with error
      processQueue(err, null);
      
      // Clear all auth data and redirect to login
      localStorage.clear();
      window.location.href = '/login?session=expired';
      
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

export default apiClient;
